import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { useAuthStore } from './store/authStore';
import { Home, Layers, DollarSign, CheckSquare, Phone, LogIn } from 'lucide-react';
import LoadingSpinner from './components/Dashboard/LoadingSpinner';

// Lazy loaded page components
const HomePage = lazy(() => import('./pages/HomePage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'));
const VideosPage = lazy(() => import('./pages/VideosPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Dashboard components
const DashboardLayout = lazy(() => import('./pages/Dashboard/DashboardLayout'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const DashboardSelection = lazy(() => import('./pages/Dashboard/DashboardSelection'));
const MbaDashboard = lazy(() => import('./pages/Dashboard/MbaDashboard'));
const LeadScoring = lazy(() => import('./pages/Dashboard/LeadScoring'));
const ProposalGenerator = lazy(() => import('./pages/Dashboard/ProposalGenerator'));
const MarketingAnalysis = lazy(() => import('./pages/Dashboard/MarketingAnalysis'));
const StrategyBuilder = lazy(() => import('./pages/Dashboard/StrategyBuilder'));
const KpiDashboard = lazy(() => import('./pages/Dashboard/KpiDashboard'));
const IdeaValidator = lazy(() => import('./pages/Dashboard/IdeaValidator'));
const Settings = lazy(() => import('./pages/Dashboard/Settings'));
const SwotAnalysis = lazy(() => import('./pages/Dashboard/SwotAnalysis'));
const EmailCampaigns = lazy(() => import('./pages/Dashboard/EmailCampaigns'));
const AssetCreator = lazy(() => import('./pages/Dashboard/AssetCreator'));
const WebhookConfig = lazy(() => import('./pages/Dashboard/WebhookConfig'));
const ContentCalendar = lazy(() => import('./pages/Dashboard/ContentCalendar'));
const UnifiedAssetGenerator = lazy(() => import('./pages/Dashboard/UnifiedAssetGenerator'));
const UltimateAssetGenerator = lazy(() => import('./pages/Dashboard/UltimateAssetGenerator'));
const PromptEnhancer = lazy(() => import('./pages/Dashboard/PromptEnhancer'));
const BusinessNews = lazy(() => import('./pages/Dashboard/BusinessNews'));
const SmsPersonalizer = lazy(() => import('./pages/Dashboard/SmsPersonalizer'));
const AutomatedCalls = lazy(() => import('./pages/Dashboard/AutomatedCalls'));

// MBA Tool imports
const CompetitorAnalysis = lazy(() => import('./pages/Dashboard/mba/CompetitorAnalysis'));
const KnowledgeBase = lazy(() => import('./pages/Dashboard/KnowledgeBase'));
const MbaDashboardHome = lazy(() => import('./pages/Dashboard/mba/MbaDashboardHome'));
const MbaSwotAnalysis = lazy(() => import('./pages/Dashboard/mba/swot-analysis'));
const AnsoffMatrix = lazy(() => import('./pages/Dashboard/mba/ansoff-matrix'));
const StpAnalysis = lazy(() => import('./pages/Dashboard/mba/stp-analysis'));
const BlueOcean = lazy(() => import('./pages/Dashboard/mba/blue-ocean'));
const UnifiedMbaDashboard = lazy(() => import('./pages/Dashboard/mba/UnifiedMbaDashboard'));
const VisionMission = lazy(() => import('./pages/Dashboard/mba/vision-mission'));
const BudgetAllocation = lazy(() => import('./pages/Dashboard/mba/budget-allocation'));
const SalesStructure = lazy(() => import('./pages/Dashboard/mba/sales-structure'));
const SlideDecks = lazy(() => import('./pages/Dashboard/mba/SlideDecks'));
const MbaKpiDashboard = lazy(() => import('./pages/Dashboard/mba/MbaKpiDashboard'));

// Loading spinner component for lazy loading
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-primary-dark">
    <LoadingSpinner size="lg" message="Loading..." />
  </div>
);

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, navigate]);
  
  if (isLoading) {
    return <PageLoader />;
  }
  
  if (!isAuthenticated) {
    return null; // Redirect will happen in the useEffect
  }
  
  return <>{children}</>;
};

const App: React.FC = () => {
  const { checkAuth, isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Load form script
    const script = document.createElement('script');
    script.src = 'https://m.cmosuccesssystems.com/js/form_embed.js';
    script.async = true;
    document.body.appendChild(script);

    // Check authentication status
    checkAuth();

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [checkAuth]);

  // Redirect from dashboard to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const isDashboardRoute = location.pathname.startsWith('/dashboard');
      if (isDashboardRoute) {
        navigate('/login');
      }
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-primary-dark text-white">
      <Routes>
        {/* Dashboard Selection */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <DashboardSelection />
            </Suspense>
          </ProtectedRoute>
        } />
        
        {/* Main CMO Dashboard */}
        <Route path="/dashboard/main" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <DashboardLayout />
            </Suspense>
          </ProtectedRoute>
        }>
          <Route index element={
            <Suspense fallback={<PageLoader />}>
              <Dashboard />
            </Suspense>
          } />
          <Route path="lead-scoring" element={
            <Suspense fallback={<PageLoader />}>
              <LeadScoring />
            </Suspense>
          } />
          <Route path="proposal-generator" element={
            <Suspense fallback={<PageLoader />}>
              <ProposalGenerator />
            </Suspense>
          } />
          <Route path="marketing-analysis" element={
            <Suspense fallback={<PageLoader />}>
              <MarketingAnalysis />
            </Suspense>
          } />
          <Route path="strategy-builder" element={
            <Suspense fallback={<PageLoader />}>
              <StrategyBuilder />
            </Suspense>
          } />
          <Route path="kpi-dashboard" element={
            <Suspense fallback={<PageLoader />}>
              <KpiDashboard />
            </Suspense>
          } />
          <Route path="idea-validator" element={
            <Suspense fallback={<PageLoader />}>
              <IdeaValidator />
            </Suspense>
          } />
          <Route path="swot-analysis" element={
            <Suspense fallback={<PageLoader />}>
              <SwotAnalysis />
            </Suspense>
          } />
          <Route path="email-campaigns" element={
            <Suspense fallback={<PageLoader />}>
              <EmailCampaigns />
            </Suspense>
          } />
          <Route path="asset-creator" element={
            <Suspense fallback={<PageLoader />}>
              <AssetCreator />
            </Suspense>
          } />
          <Route path="webhook-config" element={
            <Suspense fallback={<PageLoader />}>
              <WebhookConfig />
            </Suspense>
          } />
          <Route path="content-calendar" element={
            <Suspense fallback={<PageLoader />}>
              <ContentCalendar />
            </Suspense>
          } />
          <Route path="unified-asset-generator" element={
            <Suspense fallback={<PageLoader />}>
              <UnifiedAssetGenerator />
            </Suspense>
          } />
          <Route path="ultimate-asset-generator" element={
            <Suspense fallback={<PageLoader />}>
              <UltimateAssetGenerator />
            </Suspense>
          } />
          <Route path="prompt-enhancer" element={
            <Suspense fallback={<PageLoader />}>
              <PromptEnhancer />
            </Suspense>
          } />
          <Route path="business-news" element={
            <Suspense fallback={<PageLoader />}>
              <BusinessNews />
            </Suspense>
          } />
          <Route path="sms-personalizer" element={
            <Suspense fallback={<PageLoader />}>
              <SmsPersonalizer />
            </Suspense>
          } />
          <Route path="automated-calls" element={
            <Suspense fallback={<PageLoader />}>
              <AutomatedCalls />
            </Suspense>
          } />
          <Route path="settings" element={
            <Suspense fallback={<PageLoader />}>
              <Settings />
            </Suspense>
          } />
        </Route>
        
        {/* MBA Dashboard Routes */}
        <Route path="/dashboard/mba" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <MbaDashboard />
            </Suspense>
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard/mba/home" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <MbaDashboardHome />
            </Suspense>
          </ProtectedRoute>
        } />
        
        {/* Unified MBA Dashboard */}
        <Route path="/dashboard/mba/unified-dashboard" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <UnifiedMbaDashboard />
            </Suspense>
          </ProtectedRoute>
        } />
        
        {/* MBA Tool Routes */}
        <Route path="/dashboard/mba/competitor-analysis" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <CompetitorAnalysis />
            </Suspense>
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard/mba/swot-analysis" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <MbaSwotAnalysis />
            </Suspense>
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard/mba/ansoff-matrix" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <AnsoffMatrix />
            </Suspense>
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard/mba/stp-analysis" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <StpAnalysis />
            </Suspense>
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard/mba/blue-ocean" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <BlueOcean />
            </Suspense>
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard/mba/vision-mission" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <VisionMission />
            </Suspense>
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard/mba/kpi-dashboard" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <MbaKpiDashboard />
            </Suspense>
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard/mba/budget-allocation" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <BudgetAllocation />
            </Suspense>
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard/mba/sales-structure" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <SalesStructure />
            </Suspense>
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard/mba/slide-decks" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <SlideDecks />
            </Suspense>
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard/mba/knowledge-base" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <KnowledgeBase />
            </Suspense>
          </ProtectedRoute>
        } />

        {/* Public routes with navbar/footer */}
        <Route 
          path="/*" 
          element={
            <>
              <Navbar />
              <main className="flex-grow">
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/product" element={<ProductPage />} />
                    <Route path="/features" element={<FeaturesPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/compare" element={<ComparePage />} />
                    <Route path="/resources" element={<ResourcesPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogDetailPage />} />
                    <Route path="/videos" element={<VideosPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
              </main>
              <Footer />
            </>
          } 
        />
      </Routes>
    </div>
  );
};

export default App;